/**
 * Widget Context Utilities
 * Helps determine if code is running in embedded widget context
 * and provides appropriate Supabase client
 */

// Global flag to mark widget context
let _isWidgetContext: boolean | null = null;

/**
 * Checks if code is running in embedded widget context
 */
export function isWidgetContext(): boolean {
  // Cache the result to avoid repeated DOM queries
  if (_isWidgetContext !== null) {
    return _isWidgetContext;
  }

  // Check if we're in a widget bundle (not the main app)
  if (typeof window !== 'undefined') {
    // Widget context is detected by checking if we're in Shadow DOM or if widget is initialized
    // Also check if we're loading from widget.js
    _isWidgetContext = !!(
      window.IntentDriveWidget ||
      document.querySelector('#intentdrive-container') ||
      document.querySelector('#intentdrive-shadow-root') ||
      (typeof document !== 'undefined' && document.currentScript?.src?.includes('widget.js'))
    );
    return _isWidgetContext;
  }
  return false;
}

/**
 * Force set widget context (for testing or explicit control)
 */
export function setWidgetContext(isWidget: boolean) {
  _isWidgetContext = isWidget;
}

/**
 * Gets the appropriate Supabase client based on context
 * Returns widget client for embedded widgets, regular client for app
 */
export async function getSupabaseClient() {
  if (isWidgetContext()) {
    const { widgetSupabase } = await import('@/integrations/supabase/widgetClient');
    return widgetSupabase;
  }
  const { supabase } = await import('@/integrations/supabase/client');
  return supabase;
}

